/**
 * Storage utilities for chat history and query history
 */

/**
 * Generate a UUID v4
 * @returns {string} A UUID v4 string
 */
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Get the localStorage key for session ID
 * @param {string} endpointId - The endpoint ID
 * @returns {string} The storage key
 */
function getSessionIdKey(endpointId) {
    return `session-id-${endpointId}`;
}

/**
 * Get the localStorage key for chat history
 * @param {string} endpointId - The endpoint ID
 * @returns {string} The storage key
 */
function getChatHistoryKey(endpointId) {
    return `chat-history-${endpointId}`;
}

/**
 * Get the localStorage key for query history
 * @param {string} endpointId - The endpoint ID
 * @returns {string} The storage key
 */
function getQueryHistoryKey(endpointId) {
    return `query-history-${endpointId}`;
}

/**
 * Create a new message object
 * @param {string} type - 'user' or 'ai'
 * @param {string} content - The message content
 * @param {string} endpointId - The endpoint ID
 * @returns {Object} The message object
 */
function createMessage(type, content, endpointId) {
    // Inline content formatting to avoid async issues
    let processedContent = content;
    if (typeof content !== 'string') {
        if (typeof content === 'object' && content !== null) {
            processedContent = JSON.stringify(content, null, 2);
        } else if (content === null || content === undefined) {
            processedContent = 'Keine Antwort erhalten';
        } else {
            processedContent = String(content);
        }
    }
    
    return {
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date().toISOString(),
        type,
        content: processedContent,
        endpointId
    };
}

/**
 * Extract JSON blocks from text content
 * @param {string} content - The text content to search
 * @returns {Array} Array of JSON objects found
 */
function extractJsonBlocks(content) {
    const jsonBlocks = [];
    
    // Match JSON blocks with various patterns
    const patterns = [
        /```json\s*\n([\s\S]*?)\n```/gi,  // ```json ... ```
        /```\s*\n(\{[\s\S]*?\})\n```/gi,  // ``` { ... } ```
        /(\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\})/g  // Standalone JSON objects
    ];
    
    patterns.forEach(pattern => {
        let match;
        while ((match = pattern.exec(content)) !== null) {
            try {
                const jsonStr = match[1].trim();
                const parsed = JSON.parse(jsonStr);
                jsonBlocks.push({
                    raw: jsonStr,
                    parsed,
                    timestamp: new Date().toISOString()
                });
            } catch (e) {
                // Invalid JSON, skip
            }
        }
    });
    
    return jsonBlocks;
}

/**
 * Load chat history for an endpoint
 * @param {string} endpointId - The endpoint ID
 * @returns {Array} Array of messages
 */
export function loadChatHistory(endpointId) {
    try {
        const key = getChatHistoryKey(endpointId);
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error loading chat history:', error);
        return [];
    }
}

/**
 * Save chat history for an endpoint
 * @param {string} endpointId - The endpoint ID
 * @param {Array} messages - Array of messages
 */
export function saveChatHistory(endpointId, messages) {
    try {
        const key = getChatHistoryKey(endpointId);
        localStorage.setItem(key, JSON.stringify(messages));
    } catch (error) {
        console.error('Error saving chat history:', error);
    }
}

/**
 * Add a message to chat history
 * @param {string} endpointId - The endpoint ID
 * @param {string} type - 'user' or 'ai'
 * @param {string} content - The message content
 * @returns {Object} The created message
 */
export function addMessage(endpointId, type, content) {
    const message = createMessage(type, content, endpointId);
    const history = loadChatHistory(endpointId);
    history.push(message);
    saveChatHistory(endpointId, history);
    
    // If it's an AI message, extract and save JSON blocks
    if (type === 'ai') {
        const jsonBlocks = extractJsonBlocks(content);
        if (jsonBlocks.length > 0) {
            saveJsonBlocks(endpointId, jsonBlocks);
        }
    }
    
    return message;
}

/**
 * Load query history (JSON blocks) for an endpoint
 * @param {string} endpointId - The endpoint ID
 * @returns {Array} Array of JSON blocks
 */
export function loadQueryHistory(endpointId) {
    try {
        const key = getQueryHistoryKey(endpointId);
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error loading query history:', error);
        return [];
    }
}

/**
 * Save JSON blocks to query history
 * @param {string} endpointId - The endpoint ID
 * @param {Array} jsonBlocks - Array of JSON blocks
 */
function saveJsonBlocks(endpointId, jsonBlocks) {
    try {
        const key = getQueryHistoryKey(endpointId);
        const existing = loadQueryHistory(endpointId);
        const updated = [...existing, ...jsonBlocks];
        localStorage.setItem(key, JSON.stringify(updated));
    } catch (error) {
        console.error('Error saving query history:', error);
    }
}

/**
 * Get or create a session ID for an endpoint
 * @param {string} endpointId - The endpoint ID
 * @returns {string} The session ID
 */
export function getSessionId(endpointId) {
    try {
        const key = getSessionIdKey(endpointId);
        let sessionId = localStorage.getItem(key);
        
        if (!sessionId) {
            sessionId = generateUUID();
            localStorage.setItem(key, sessionId);
        }
        
        return sessionId;
    } catch (error) {
        console.error('Error getting session ID:', error);
        return generateUUID(); // Fallback to new UUID
    }
}

/**
 * Clear session ID for an endpoint (generates new one on next access)
 * @param {string} endpointId - The endpoint ID
 */
export function clearSessionId(endpointId) {
    try {
        localStorage.removeItem(getSessionIdKey(endpointId));
    } catch (error) {
        console.error('Error clearing session ID:', error);
    }
}

/**
 * Clear all history for an endpoint
 * @param {string} endpointId - The endpoint ID
 */
export function clearHistory(endpointId) {
    try {
        localStorage.removeItem(getChatHistoryKey(endpointId));
        localStorage.removeItem(getQueryHistoryKey(endpointId));
        clearSessionId(endpointId); // Also clear session ID
    } catch (error) {
        console.error('Error clearing history:', error);
    }
}

/**
 * Get all stored endpoint IDs
 * @returns {Array} Array of endpoint IDs that have stored data
 */
export function getStoredEndpoints() {
    const endpoints = new Set();
    
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith('chat-history-')) {
            const endpointId = key.replace('chat-history-', '');
            endpoints.add(endpointId);
        }
    }
    
    return Array.from(endpoints);
}
